import React, { useState } from 'react';
import { Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbApi } from '@/services/ewayBillApi';
import { fmtDT } from '@/components/ewayBill/shared';

const show = ({ blob, name }, win) => {
  const url = URL.createObjectURL(blob);
  const tab = !!win && !win.closed;
  if (tab) win.location.href = url;
  else {
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return tab;
};

const AgainDialog = ({ again, total, onClose, onPrint }) => (
  <Dialog open={!!again.length} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg" data-testid="ewb-reprint-dialog">
      <DialogHeader><DialogTitle data-testid="ewb-reprint-title">{again.length} already printed — print again?</DialogTitle></DialogHeader>
      <ul className="max-h-60 divide-y overflow-y-auto rounded-lg border text-sm" data-testid="ewb-reprint-list">
        {again.map((b) => (
          <li key={b.id} className="px-3 py-1.5"><span className="font-mono font-semibold">{b.ewb_no}</span> <span className="text-gray-500">{b.doc_no}</span>
            <div className="text-[11px] text-sky-700">Printed {fmtDT(b.printed.at)} · {b.printed.by_name || '—'}{b.printed.count > 1 ? ` · ${b.printed.count}×` : ''}</div></li>
        ))}
      </ul>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="ghost" onClick={onClose} data-testid="ewb-reprint-cancel">Cancel</Button>
        {total > again.length && <Button variant="outline" onClick={() => onPrint(false)} data-testid="ewb-reprint-skip">Skip printed — print {total - again.length}</Button>}
        <Button onClick={() => onPrint(true)} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="ewb-reprint-all">Print all {total}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

/** Ticked bills with an EWB No → one merged PDF opened in a new tab; already-printed bills ask first. */
export const BulkPrintBar = ({ rows, picked, setPicked, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [again, setAgain] = useState([]);
  const printable = rows.filter((b) => b.ewb_no);
  const imported = printable.filter((b) => b.portal_import && !b.printed);
  const ticked = printable.filter((b) => picked.has(b.id));
  const run = (list) => {
    setAgain([]);
    const win = window.open('', '_blank');
    setBusy(true);
    ewbApi.bulkPdf(list.map((b) => b.id))
      .then((r) => { toast.success(`${r.count} e-way bill(s) in one PDF — ${show(r, win) ? 'print from the new tab' : 'downloaded, open it to print'}`); setPicked(new Set()); onDone(); })
      .catch((e) => { win?.close(); toast.error(errMsg(e, 'Bulk print failed')); }).finally(() => setBusy(false));
  };
  const print = () => {
    const done = ticked.filter((b) => b.printed);
    if (done.length) setAgain(done); else run(ticked);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/50 px-3 py-2" data-testid="ewb-bulk-print-bar">
      <Button size="sm" variant="outline" onClick={() => setPicked(new Set(imported.map((b) => b.id)))} disabled={!imported.length} data-testid="ewb-pick-all-imported">
        Select all imported from portal — not printed ({imported.length})</Button>
      {picked.size > 0 && <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())} data-testid="ewb-print-pick-clear">Clear</Button>}
      <Button size="sm" onClick={print} disabled={!ticked.length || busy} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="ewb-bulk-print-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}Bulk print ({ticked.length})</Button>
      <span className="text-xs text-gray-600">Tick the bills to print — they come as one PDF, each bill on a new page.</span>
      <AgainDialog again={again} total={ticked.length} onClose={() => setAgain([])} onPrint={(all) => run(all ? ticked : ticked.filter((b) => !b.printed))} />
    </div>
  );
};

export default BulkPrintBar;
